import BACKOFFICE_BASE_API_V1 from "./backofficeApi";

const DEFAULT_ERROR_MESSAGE =
  "Наидовме на проблем. Обидете се повторно за неколку моменти.";

const ABSOLUTE_URL_PATTERN = /^https?:\/\//i;

const parseErrorResponse = async (response, fallbackMessage) => {
  if (!response) {
    throw new Error(fallbackMessage);
  }

  let payload = null;
  const clonedResponse = response.clone();

  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
    throw new Error(payload.errors.join(" "));
  }

  if (typeof payload?.error === "string" && payload.error.trim()) {
    throw new Error(payload.error);
  }

  const text = await clonedResponse.text().catch(() => "");
  throw new Error(text || fallbackMessage);
};

const toQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    if (Array.isArray(value)) {
      value
        .filter((entry) => entry !== undefined && entry !== null && entry !== "")
        .forEach((entry) => searchParams.append(key, String(entry)));
      return;
    }

    searchParams.set(key, String(value));
  });

  const query = searchParams.toString();
  return query ? `?${query}` : "";
};

export const resolveApiEndpoint = (endpoint) => {
  if (!endpoint) {
    return BACKOFFICE_BASE_API_V1;
  }

  if (ABSOLUTE_URL_PATTERN.test(endpoint)) {
    return endpoint;
  }

  const baseUrl = new URL(BACKOFFICE_BASE_API_V1);

  if (endpoint.startsWith("/")) {
    return new URL(endpoint, baseUrl.origin).toString();
  }

  return new URL(endpoint, `${BACKOFFICE_BASE_API_V1}/`).toString();
};

async function fetchJson(url, options = {}, fallbackMessage = DEFAULT_ERROR_MESSAGE) {
  let response;

  try {
    response = await fetch(url, {
      ...options,
      headers: {
        Accept: "application/json",
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...(options.headers || {}),
      },
    });
  } catch (error) {
    throw new Error(fallbackMessage);
  }

  if (!response.ok) {
    await parseErrorResponse(response, fallbackMessage);
  }

  if (response.status === 204) {
    return null;
  }

  return response.json().catch(() => null);
}

export function getVenues(params = {}) {
  return fetchJson(
    `${BACKOFFICE_BASE_API_V1}/venues${toQueryString(params)}`,
    {},
    "Неуспешно вчитување на понудувачите."
  );
}

export function getVenueDetail(venueId, params = {}) {
  return fetchJson(
    `${BACKOFFICE_BASE_API_V1}/venues/${venueId}${toQueryString(params)}`,
    {},
    "Неуспешно вчитување на деталите за понудувачот."
  );
}

export function getVenueProducts(venueId, params = {}) {
  return fetchJson(
    `${BACKOFFICE_BASE_API_V1}/venues/${venueId}/products${toQueryString(params)}`,
    {},
    "Неуспешно вчитување на продуктите."
  );
}

export function getVenueAvailability(venueId, params = {}) {
  return fetchJson(
    `${BACKOFFICE_BASE_API_V1}/venues/${venueId}/availability${toQueryString(params)}`,
    {},
    "Неуспешно вчитување на достапноста."
  );
}

export function getProductDetail(productId, params = {}) {
  return fetchJson(
    `${BACKOFFICE_BASE_API_V1}/products/${productId}${toQueryString(params)}`,
    {},
    "Неуспешно вчитување на продуктот."
  );
}

export function submitLead(payload, endpoint) {
  return fetchJson(resolveApiEndpoint(endpoint || "/api/v1/leads"), {
    method: "POST",
    body: JSON.stringify({ lead: payload }),
  });
}

export function submitReview(venueId, payload, endpoint) {
  const target =
    endpoint || `${BACKOFFICE_BASE_API_V1}/venues/${venueId}/reviews`;

  return fetchJson(resolveApiEndpoint(target), {
    method: "POST",
    body: JSON.stringify({ review: payload }),
  });
}

export function submitBookingRequest(venueId, payload) {
  return fetchJson(
    `${BACKOFFICE_BASE_API_V1}/venues/${venueId}/booking_requests`,
    {
      method: "POST",
      body: JSON.stringify({ booking_request: payload }),
    }
  );
}

export function trackContactClick(payload, endpoint) {
  return fetchJson(resolveApiEndpoint(endpoint || "/api/v1/contact_clicks"), {
    method: "POST",
    body: JSON.stringify({ contact_click: payload }),
  }).catch((error) => {
    console.error("CTA tracking failed:", error);
    return null;
  });
}
